import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Loader2, Sparkles, Check, AlertCircle, Package, RefreshCcw, ExternalLink } from 'lucide-react';
import type { Email } from '@/types/email';
import { Button } from '@/components/ui/button';
import { CreationPopover } from './CreationPopover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProductLineExtraction } from '@/hooks/useProductLineExtraction';
import { getClickUpLists, createClickUpProductLine, type ClickUpList, type ClickUpTask } from '@/api/integrationApi';
import { cn } from '@/lib/utils';
import { formatDateOnly, parseDateOnly } from '@/features/todo/utils/dateHelpers';

type AnchorElement = HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element };

interface EmailProductLineCreationContextValue {
  email: Email;
  extraction: ReturnType<typeof useProductLineExtraction>;
  openPopover: (anchor: AnchorElement) => void;
  closePopover: () => void;
  popoverState: {
    open: boolean;
    anchorRef: React.RefObject<{ getBoundingClientRect: () => DOMRect; contextElement?: Element } | null>;
  };
}

const EmailProductLineCreationContext = createContext<EmailProductLineCreationContextValue | null>(null);

export function EmailProductLineCreationProvider({
  email,
  children,
}: {
  email: Email;
  children: ReactNode;
}) {
  const extraction = useProductLineExtraction(email);
  const [anchorState, setAnchorState] = useState<{
    element: AnchorElement | null;
    key: number;
  }>({
    element: null,
    key: 0,
  });
  const anchor = anchorState.element;
  const anchorRef = useRef<{ getBoundingClientRect: () => DOMRect; contextElement?: Element } | null>(null);
  const autoRequestRef = useRef(false);

  useEffect(() => {
    if (!anchor) {
      anchorRef.current = null;
      return;
    }
    // Only include contextElement if anchor is an actual HTMLElement still in DOM
    const contextElement = anchor instanceof HTMLElement && anchor.isConnected ? anchor : undefined;
    anchorRef.current = {
      getBoundingClientRect: () => anchor.getBoundingClientRect(),
      contextElement,
    };
  }, [anchorState.key, anchor]);

  useEffect(() => {
    if (!anchor) {
      autoRequestRef.current = false;
    }
  }, [anchorState.key, anchor]);

  // Auto-run extraction when popover opens
  useEffect(() => {
    if (!anchor) {
      return;
    }
    if (extraction.isLoading) {
      return;
    }
    if (extraction.extraction) {
      autoRequestRef.current = true;
      return;
    }
    if (autoRequestRef.current) {
      return;
    }
    autoRequestRef.current = true;
    void extraction.requestExtraction();
  }, [anchorState.key, anchor, extraction]);

  const closePopover = useCallback(() => {
    setAnchorState((prev) => ({ ...prev, element: null }));
  }, []);

  const openPopover = useCallback((target: AnchorElement) => {
    setAnchorState({ element: target, key: Date.now() });
  }, []);

  const contextValue = useMemo<EmailProductLineCreationContextValue>(
    () => ({
      email,
      extraction,
      openPopover,
      closePopover,
      popoverState: {
        open: Boolean(anchor),
        anchorRef,
      },
    }),
    [email, extraction, openPopover, closePopover, anchor]
  );

  return (
    <EmailProductLineCreationContext.Provider value={contextValue}>
      {children}
      <EmailProductLineCreationPopover />
    </EmailProductLineCreationContext.Provider>
  );
}

export function useEmailProductLineCreation() {
  const ctx = useContext(EmailProductLineCreationContext);
  if (!ctx) {
    throw new Error('useEmailProductLineCreation must be used within EmailProductLineCreationProvider');
  }
  return ctx;
}

/**
 * Consumer component that provides openPopover callback to children
 * Useful for integrating with dropdown menus
 */
export function ProductLineCreationConsumer({
  children,
}: {
  children: (props: { openPopover: (anchor: AnchorElement) => void }) => ReactNode;
}) {
  const ctx = useContext(EmailProductLineCreationContext);
  if (!ctx) {
    return null;
  }
  return <>{children({ openPopover: ctx.openPopover })}</>;
}

/**
 * Hook to get the openPopover function, returns null if not in provider context
 */
export function useProductLineCreationTrigger(): ((anchor: AnchorElement) => void) | null {
  const ctx = useContext(EmailProductLineCreationContext);
  return ctx?.openPopover ?? null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalEmailProductLineCreation() {
  return useContext(EmailProductLineCreationContext);
}

export function EmailProductLineCreationIconButton({
  className,
  variant = 'ghost',
  size = 'icon',
}: {
  className?: string;
  variant?: 'outline' | 'ghost';
  size?: 'icon' | 'sm';
}) {
  const ctx = useOptionalEmailProductLineCreation();
  if (!ctx) {
    return null;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={(event) => ctx.openPopover(event.currentTarget)}
        >
          <Package className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Create product line</TooltipContent>
    </Tooltip>
  );
}

function EmailProductLineCreationPopover() {
  const ctx = useContext(EmailProductLineCreationContext);

  if (!ctx) {
    return null;
  }

  const { email, extraction, popoverState, closePopover } = ctx;

  // Form state
  const [productLine, setProductLine] = useState('');
  const [description, setDescription] = useState('');
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);

  // ClickUp lists state
  const [lists, setLists] = useState<ClickUpList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listsError, setListsError] = useState<string | null>(null);

  // Creation state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdTask, setCreatedTask] = useState<ClickUpTask | null>(null);

  // Track email to reset form on email change
  const [initialEmailId, setInitialEmailId] = useState<string | null>(null);

  // Reset form when email changes
  useEffect(() => {
    if (initialEmailId !== email.id) {
      setInitialEmailId(email.id);
      setProductLine('');
      setDescription('');
      setSelectedListId(null);
      setDueDate(null);
      setCreatedTask(null);
      setCreateError(null);
    }
  }, [email.id, initialEmailId]);

  // Pre-fill form from extraction
  useEffect(() => {
    if (extraction.extraction && !createdTask) {
      const ext = extraction.extraction;
      if (ext.productLine && !productLine) {
        setProductLine(ext.productLine);
      }
      if (ext.description && !description) {
        setDescription(ext.description);
      }
      if (ext.orderDeadline && !dueDate) {
        setDueDate(ext.orderDeadline);
      }
      // Try to match manufacturer to a list
      if (ext.manufacturer && !selectedListId && lists.length > 0) {
        const manufacturerLower = ext.manufacturer.toLowerCase();
        const match = lists.find((list) =>
          list.name.toLowerCase().includes(manufacturerLower) ||
          manufacturerLower.includes(list.name.toLowerCase())
        );
        if (match) {
          setSelectedListId(match.id);
        }
      }
    }
  }, [extraction.extraction, createdTask, productLine, description, dueDate, selectedListId, lists]);

  // Fetch ClickUp lists when popover opens
  useEffect(() => {
    if (!popoverState.open || lists.length > 0 || listsLoading) {
      return;
    }
    setListsLoading(true);
    setListsError(null);
    getClickUpLists()
      .then((response) => {
        setLists(response.lists);
      })
      .catch((err) => {
        setListsError(err instanceof Error ? err.message : 'Failed to load manufacturers');
      })
      .finally(() => {
        setListsLoading(false);
      });
  }, [popoverState.open, lists.length, listsLoading]);

  const handleCreate = useCallback(async () => {
    if (!selectedListId || !productLine.trim()) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createClickUpProductLine({
        listId: selectedListId,
        name: productLine.trim(),
        description: description.trim() || undefined,
        dueDate: dueDate || undefined,
        sourceEmail: {
          accountId: Number(email.accountId),
          messageId: Number(email.id),
        },
      });
      setCreatedTask(result.task);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create product line');
    } finally {
      setCreating(false);
    }
  }, [selectedListId, productLine, description, dueDate, email.accountId, email.id]);

  const canCreate = Boolean(selectedListId && productLine.trim() && !creating && !createdTask);

  return (
    <CreationPopover
      open={popoverState.open}
      onClose={closePopover}
      title="Create product line"
      icon={<Package className="h-4 w-4 text-muted-foreground" />}
      headerActions={
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1.5 text-xs"
          onClick={() => extraction.requestExtraction({ force: true })}
          disabled={extraction.isLoading}
        >
          <RefreshCcw className={cn('h-3 w-3', extraction.isLoading && 'animate-spin')} />
          {extraction.isLoading ? 'Extracting...' : 'Re-extract'}
        </Button>
      }
      anchorRef={popoverState.anchorRef}
      align="end"
      side="bottom"
      desktopClassName="w-[400px] max-w-[400px]"
    >
      <div className="space-y-4">

          {/* Success State */}
          {createdTask ? (
            <div className="rounded-lg border border-success-line bg-success-surface p-4 space-y-3">
              <div className="flex items-center gap-2 text-success-ink">
                <Check className="h-4 w-4" />
                <span className="text-sm font-medium">Product line created!</span>
              </div>
              <p className="text-sm text-success-ink">{createdTask.name}</p>
              {createdTask.url && (
                <a
                  href={createdTask.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-success-ink hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open in ClickUp
                </a>
              )}
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-2"
                onClick={() => {
                  setCreatedTask(null);
                  setProductLine('');
                  setDescription('');
                  setDueDate(null);
                }}
              >
                Create Another
              </Button>
            </div>
          ) : (
            <>
              {/* Extraction Status */}
              {extraction.isLoading && !extraction.extraction && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Extracting product details...</span>
                </div>
              )}

              {extraction.error && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>{extraction.error}</span>
                </div>
              )}

              {extraction.extraction && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Sparkles className="h-3 w-3" />
                  <span>
                    AI detected: {extraction.extraction.manufacturer || 'Unknown manufacturer'}
                    {extraction.extraction.confidence !== null && (
                      <span className="ml-1">
                        ({Math.round(extraction.extraction.confidence * 100)}% confidence)
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Form */}
              <div className="space-y-3">
                {/* Manufacturer Select */}
                <div className="space-y-1.5">
                  <Label htmlFor="manufacturer" className="text-xs">
                    Manufacturer
                  </Label>
                  <Select
                    value={selectedListId || ''}
                    onValueChange={(value) => setSelectedListId(value || null)}
                    disabled={listsLoading}
                  >
                    <SelectTrigger id="manufacturer" className="h-9">
                      <SelectValue
                        placeholder={listsLoading ? 'Loading...' : 'Select manufacturer'}
                      />
                    </SelectTrigger>
                    <SelectContent onEscapeKeyDown={(e) => e.stopPropagation()}>
                      {lists.map((list) => (
                        <SelectItem key={list.id} value={list.id}>
                          {list.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {listsError && (
                    <p className="text-xs text-destructive">{listsError}</p>
                  )}
                </div>

                {/* Product Line Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="productLine" className="text-xs">
                    Product Line Name
                  </Label>
                  <Input
                    id="productLine"
                    value={productLine}
                    onChange={(e) => setProductLine(e.target.value)}
                    placeholder="e.g., Summer 2025 Collection"
                    className="h-9"
                  />
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <Label htmlFor="description" className="text-xs">
                    Description (optional)
                  </Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of the product line"
                    className="min-h-[80px] resize-none"
                  />
                </div>

                {/* Due Date */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Order Deadline (optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={dueDate ? formatDateOnly(parseDateOnly(dueDate)!) : ''}
                      onChange={(e) => {
                        const parsed = parseDateOnly(e.target.value);
                        setDueDate(parsed ? formatDateOnly(parsed) : null);
                      }}
                      placeholder="YYYY-MM-DD"
                      className="h-9 flex-1"
                    />
                    {dueDate && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-9 px-2"
                        onClick={() => setDueDate(null)}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Error */}
              {createError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>{createError}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={closePopover}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!canCreate}
                  className="gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4" />
                      Create in ClickUp
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
      </div>
    </CreationPopover>
  );
}
